import { useEffect, useMemo, useRef, useState } from "react";

import { CSS } from "@dnd-kit/utilities";
import { AgCharts } from "ag-charts-react";
import { AgGridReact } from "ag-grid-react";
import { Input, message, Select } from "antd";
import { Bubble, Chart, Scatter } from "react-chartjs-2";
import { GaugeComponent } from 'react-gauge-component';
import { FaExpand, FaPlus, FaTimes } from "react-icons/fa";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";
import { NumberFilterModule, ModuleRegistry, ClientSideRowModelModule, TextFilterModule, ValidationModule, PaginationModule, ClientSideRowModelApiModule } from "ag-grid-community";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, rectSortingStrategy, } from "@dnd-kit/sortable";
import { Chart as ChartJS, CategoryScale, Tooltip, Legend, PointElement, LinearScale, Title, } from "chart.js";

import "./styles.scss";
import { esgData } from "./chart-data";
import CustomModal from "../../assets/component/modal";
import Breadcrumbs from "../../assets/component/breadcrumbs";

ChartJS.register(Tooltip, Legend, CategoryScale, MatrixController, MatrixElement, PointElement, LinearScale, Title);

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  NumberFilterModule,
  TextFilterModule,
  ValidationModule,
  ClientSideRowModelApiModule,
  PaginationModule,
]);

const Card = () => {
  const totalCompanies = 11;

  const cardData = [
    {
      id: 1,
      title: "Total Companies",
      value: totalCompanies
    },
    {
      id: 2,
      title: "Average ESG Score",
      value: (esgData?.reduce((acc, curr) => acc + curr.ESG_Overall, 0) / (totalCompanies * 10)).toFixed(2)
    },
    {
      id: 3,
      title: "Average Revenue",
      value: "$" + (esgData?.reduce((acc, curr) => acc + curr.Revenue, 0) / (totalCompanies * 10)).toFixed(2)
    },
    {
      id: 4,
      title: "Average Growth Rate",
      value: (esgData?.reduce((acc, curr) => acc + (curr.GrowthRate || 0), 0) / (totalCompanies * 10)).toFixed(2) + "%"
    },
    {
      id: 5,
      title: "Average Profit Margin",
      value: (esgData?.reduce((acc, curr) => acc + curr.ProfitMargin, 0) / (totalCompanies * 10)).toFixed(2) + "%"
    }]

  return (
    <div className="card-container">
      {cardData?.map(card => (
        <div key={card?.id} className="card">
          <div className="card-header">{card?.title}</div>
          <div className="card-body">{card?.value}</div>
        </div>
      ))}
    </div>
  )
}

const AddChartModal = ({ handleCreateChart }) => {
  const [formValues, setFormValues] = useState({
    chartName: "",
    chartType: null,
    radialScale: null,
    xAxis: null,
    yAxis: null
  });

  const singleValueChart = formValues?.chartType === "radialGauge" || formValues?.chartType === "heatmap"

  const chartOptions = [
    { label: "Radial Gauge", value: "radialGauge" },
    { label: "Horizontal Bar Chart", value: "bar" },
    { label: "Stacked Bar Chart", value: "stackedBar" },
    { label: "Scatter Plot", value: "scatter" },
    { label: "Bubble Chart", value: "bubble" },
    { label: "Heatmap", value: "heatmap" }
  ];

  const axisOptions = [
    { label: "Company Name", value: "CompanyName" },
    { label: "Year", value: "Year" },
    { label: "Industry", value: "Industry" },
    { label: "Region", value: "Region" },
    { label: "Revenue", value: "Revenue" },
    { label: "Profit Margin", value: "ProfitMargin" },
    { label: "Market Cap", value: "MarketCap" },
    { label: "Growth Rate", value: "GrowthRate" },
    { label: "ESG Overall", value: "ESG_Overall" },
    { label: "Carbon Emissions", value: "CarbonEmissions" },
    { label: "Water Usage", value: "WaterUsage" },
    { label: "Energy Consumption", value: "EnergyConsumption" },
    { label: "ESG Environmental", value: "ESG_Environmental" },
    { label: "ESG Social", value: "ESG_Social" },
    { label: "ESG Governance", value: "ESG_Governance" },
  ]

  const filteredXAxisOptions = axisOptions?.filter(option => {
    if (formValues?.chartType === "scatter" || formValues?.chartType === "bubble" || formValues?.chartType === "stackedBar" || formValues?.chartType === "heatmap" || formValues?.chartType === "bar") {
      return option?.value !== "CompanyName" && option?.value !== "Year" && option?.value !== "Industry" && option?.value !== "Region" && option?.value !== formValues?.yAxis;
    } else if (formValues?.chartType === "radialGauge") {
      return option?.value !== "CompanyName" && option?.value !== "Year" && option?.value !== "Industry" && option?.value !== "Region" && option?.value !== formValues?.yAxis;
    }
    return option?.value !== formValues?.yAxis;
  });

  const filteredYAxisOptions = axisOptions?.filter(option => {
    if (formValues?.chartType === "scatter" || formValues?.chartType === "bubble" || formValues?.chartType === "stackedBar" || formValues?.chartType === "bar") {
      return option?.value !== "CompanyName" && option?.value !== "Year" && option?.value !== "Industry" && option?.value !== "Region" && option?.value !== formValues?.xAxis;
    }
    return option?.value !== formValues?.xAxis;
  });

  const onFieldChange = (field, value) => {
    const finalValue = value?.target?.value ?? value;
    setFormValues(prev => ({
      ...prev,
      [field]: finalValue
    }));
  }

  return (
    <div className="add-chart-modal">
      <div className="input-wrapper">
        <div className="input-label">
          Chart Name <span>*</span>
        </div>
        <div className="input-container">
          <Input className="filter-select" onChange={(e) => { onFieldChange("chartName", e) }} value={formValues?.chartName} placeholder="Enter Chart Name" />
        </div>
      </div>
      <div className="input-wrapper">
        <div className="input-label">
          Chart Type <span>*</span>
        </div>
        <div className="input-container">
          <Select
            showSearch
            allowClear
            className="filter-select"
            options={chartOptions}
            onChange={(e) => { onFieldChange("chartType", e) }}
            value={formValues?.chartType}
            placeholder="Select Chart"
            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
          />
        </div>
      </div>
      <div className="input-wrapper">
        <div className="input-label">
          {formValues?.chartType === "stackedBar" ? "Stack Value" : formValues?.chartType === "bar" ? "Bar Value" : singleValueChart ? "Metric" : "X-axis"} <span>*</span>
        </div>
        <div className="input-container">
          <Select
            showSearch
            allowClear
            className="filter-select"
            options={filteredXAxisOptions}
            onChange={(e) => { onFieldChange(singleValueChart ? "radialScale" : "xAxis", e) }}
            value={singleValueChart ? formValues?.radialScale : formValues?.xAxis}
            placeholder="Select Value"
            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
          />
        </div>
      </div>
      {!singleValueChart && (<div className="input-wrapper">
        <div className="input-label">
          {formValues?.chartType === "stackedBar" ? "Stack Value" : formValues?.chartType === "bar" ? "Bar Value" : "Y-axis"} <span>*</span>
        </div>
        <div className="input-container">
          <Select
            showSearch
            allowClear
            className="filter-select"
            options={filteredYAxisOptions}
            onChange={(e) => { onFieldChange("yAxis", e) }}
            value={formValues?.yAxis}
            placeholder="Select Value"
            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
          />
        </div>
      </div>)}
      <div className="button-wrapper create" onClick={() => handleCreateChart(formValues)}>Create</div>
    </div>
  )
}

const Dashboard = () => {
  const [displayedData, setDisplayedData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAddChartModal, setShowAddChartModal] = useState(false);
  const [activeChart, setActiveChart] = useState(null);
  const [chartOrder, setChartOrder] = useState([
    "radialGauge",
    "bar",
    "stackedBar",
    "scatter",
    "bubble",
    "heatmap",
  ]);
  const [visibleCharts, setVisibleCharts] = useState({
    radialGauge: true,
    bar: true,
    stackedBar: true,
    scatter: true,
    bubble: true,
    heatmap: true,
  });
  const [colDefs] = useState([
    { field: "CompanyName", filter: "agTextColumnFilter" },
    { field: "Industry", filter: "agTextColumnFilter" },
    { field: "Region", filter: "agTextColumnFilter" },
    { field: "Year", filter: "agNumberColumnFilter" },
    { field: "Revenue", filter: "agNumberColumnFilter" },
    { field: "ProfitMargin", filter: "agNumberColumnFilter" },
    { field: "MarketCap", filter: "agNumberColumnFilter" },
    { field: "GrowthRate", filter: "agNumberColumnFilter" },
    { field: "ESG_Overall", filter: "agNumberColumnFilter" },
    { field: "ESG_Environmental", filter: "agNumberColumnFilter" },
    { field: "ESG_Social", filter: "agNumberColumnFilter" },
    { field: "ESG_Governance", filter: "agNumberColumnFilter" },
    { field: "CarbonEmissions", filter: "agNumberColumnFilter" },
    { field: "WaterUsage", filter: "agNumberColumnFilter" },
    { field: "EnergyConsumption", filter: "agNumberColumnFilter" },
  ]);
  const [dynamicConfigs, setDynamicConfigs] = useState({});

  const gridRef = useRef();

  const defaultColDef = { sortable: true, filter: "agNumberColumnFilter" };
  const onGridReady = () => updateDisplayedData();

  const SortableChartCard = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });

    const rootStyle = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div className="sortable-wrapper" ref={setNodeRef} style={rootStyle} aria-roledescription="sortable item">
        <div className="sortable-content" style={{ position: "relative", width: "100%", height: "100%" }}>
          <div className="sortable-drag-handle" {...attributes} {...listeners} aria-label="drag-handle">
            ≡
          </div>
          {children}
        </div>
      </div>
    );
  };

  const renderCard = (chartKey, chartComponent, extraContent = null) => {
    if (!visibleCharts[chartKey]) {
      return (
        <div className="chart-card">
          <div className="add-chart-button" onClick={() => {
            setShowAddChartModal(true);
            setActiveChart(chartKey);
          }}>
            <FaPlus /> Add Chart
          </div>
        </div>
      );
    }

    return (
      <div className="custom-chart-card">
        <div className="delete-chart-button" onClick={() => handleRemoveChart(chartKey)}><FaTimes /></div>
        <div className="expand-button" onClick={() => handleMaximize(chartKey)}><FaExpand /></div>
        <div className="chart-content">
          <div className="chart-title">
            {extraContent}
          </div>
          {chartComponent}
        </div>
      </div>
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setChartOrder((items) => {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleMaximize = (chartKey) => {
    setActiveChart(chartKey);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setActiveChart(null);
  };

  const handleRemoveChart = (chartKey) => {
    setVisibleCharts(prev => ({
      ...prev,
      [chartKey]: false
    }));

    // if (chartKey.startsWith("custom_")) {
    //   setDynamicConfigs(prev => {
    //     const next = { ...prev };
    //     delete next[chartKey];
    //     return next;
    //   });
    // }
  };

  const validateChartForm = (formValues) => {
    const requiredFields = ["chartName", "chartType", "xAxis", "yAxis"];
    if (formValues?.chartType === "radialGauge" || formValues?.chartType === "heatmap") {

      if (!formValues?.chartName?.trim() || !formValues?.radialScale) {
        message.error("All fields are mandatordy");
        return false;
      }
    } else {
      for (const field of requiredFields) {
        if (!formValues[field]?.toString().trim()) {
          message.error("All fields are mandatory");
          return false;
        }
      }
    }
    return true;
  }

  const handleCreateChart = (values) => {
    if (!validateChartForm(values)) return;
    const chartKey = activeChart;

    const { chartType, chartName, xAxis, yAxis, radialScale } = values;

    let newConfig = null;

    if (chartType === "scatter") {
      newConfig = buildScatterConfig(chartName, xAxis, yAxis);
    } else if (chartType === "bubble") {
      newConfig = buildBubbleConfig(chartName, xAxis, yAxis, "ESG_Overall");
    } else if (chartType === "bar") {
      newConfig = buildBarConfig(chartName, xAxis, yAxis);
    } else if (chartType === "stackedBar") {
      newConfig = buildStackBarConfig(chartName, xAxis, yAxis);
    } else if (chartType === "radialGauge") {
      newConfig = buildRadialConfig(chartName, radialScale);
    } else if (chartType === "heatmap") {
      newConfig = buildHeatmapConfig(chartName, radialScale)
    }

    setDynamicConfigs(prev => ({
      ...prev,
      [chartKey]: newConfig
    }));

    setVisibleCharts(prev => ({
      ...prev,
      [chartKey]: true
    }));

    setShowAddChartModal(false);
    setActiveChart(null);
  };


  const updateDisplayedData = () => {
    if (gridRef.current && gridRef.current.api) {
      const displayedRows = [];
      gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
        displayedRows.push(node.data);
      });
      setDisplayedData(displayedRows);
    }
  };

  const avgESG =
    displayedData.length > 0
      ? displayedData.reduce((sum, d) => sum + (d.ESG_Overall || 0), 0) /
      displayedData.length
      : 0;


  const baseChartConfigs = useMemo(() => {
    const uniqueCompanies = [...new Set(displayedData.map(d => d.CompanyName))];
    const uniqueYears = [...new Set(displayedData.map(d => d.Year))];

    const minRadius = 5;
    const maxRadius = 30;
    const esgMin =
      displayedData.length > 0 ? Math.min(...displayedData.map((d) => d.ESG_Overall || 0)) : 0;
    const esgMax =
      displayedData.length > 0 ? Math.max(...displayedData.map((d) => d.ESG_Overall || 0)) : 1;
    return {
      radialGauge: {
        value: avgESG,
        type: "radial",
        name: "ESG Performance Guage",
        labels: {
          tickLabels: {
            type: "inner",
            ticks: [
              { value: 20 },
              { value: 40 },
              { value: 60 },
              { value: 80 },
              { value: 100 }
            ]
          }
        },
        arc: {
          colorArray: ['#5BE12C', '#EA4228'],
          subArcs: [
            { limit: 10 },
            { limit: 30 },
            {},
            {},
            {}
          ],
          padding: 0.02,
          width: 0.3
        },

        pointer: {
          elastic: true,
          animationDelay: 0
        }
      },
      bar: {
        data: displayedData,
        type: "bar",
        name: "Company-wise Energy, Water, and Carbon Metrics",
        series: [
          {
            type: "bar",
            direction: "horizontal",
            xKey: "CompanyName",
            yKey: "EnergyConsumption",
            yName: "Energy Consumption",
          },
          {
            type: "bar",
            direction: "horizontal",
            xKey: "CompanyName",
            yKey: "WaterUsage",
            yName: "Water Usage",
          },
          {
            type: "bar",
            direction: "horizontal",
            xKey: "CompanyName",
            yKey: "CarbonEmissions",
            yName: "Carbon Emissions",
          },
        ],
        axes: [
          {
            type: "number",
            position: "top",
            max: 10000000,
          },
          {
            type: "category",
            position: "left",
          },
        ],
      },
      stackedBar: {
        data: displayedData,
        type: "stackedBar",
        name: "Stacked ESG Component Distribution",
        series: [
          {
            type: "bar",
            xKey: "CompanyName",
            yKey: "ESG_Environmental",
            yName: "Environmental",
            stacked: true,
          },
          {
            type: "bar",
            xKey: "CompanyName",
            yKey: "ESG_Social",
            yName: "Social",
            stacked: true,
          },
          {
            type: "bar",
            xKey: "CompanyName",
            yKey: "ESG_Governance",
            yName: "Governance",
            stacked: true,
          },
        ],
        legend: { position: "bottom" },
      },
      heatmap: {
        type: "matrix",
        name: "Overall ESG Heatmap",

        data: {
          datasets: [
            {
              data: displayedData.map((d) => ({
                x: d.CompanyName,
                y: String(d.Year),
                v: d.ESG_Overall
              })),

              // Dynamically scale cell width/height
              width: ({ chart }) =>
                chart?.chartArea
                  ? chart.chartArea.width / uniqueCompanies.length - 2
                  : 10,

              height: ({ chart }) =>
                chart?.chartArea
                  ? chart.chartArea.height / uniqueYears.length - 1
                  : 10,

              backgroundColor(ctx) {
                const v = ctx.raw?.v;
                if (v == null) return "transparent";

                const values = displayedData
                  .map(x => x.ESG_Overall)
                  .filter(n => typeof n === "number");

                const min = values.length ? Math.min(...values) : 0;
                const max = values.length ? Math.max(...values) : 1;

                if (max === min) return "#b2df8a";
                const t = (v - min) / (max - min);
                if (t < 0.33) return "#4CAF50";
                if (t < 0.66) return "#FFEB3B";
                if (t < 0.85) return "#FF9800";
                return "#D32F2F";
              }
            }
          ]
        },

        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label(ctx) {
                  const raw = ctx.raw;
                  return [
                    `Company: ${raw.x}`,
                    `Year: ${raw.y}`,
                    `Energy Consumption: ${raw.v}`
                  ];
                }
              }
            }
          },

          scales: {
            x: {
              type: "category",
              labels: uniqueCompanies,
              title: { display: true, text: "Company" }
            },
            y: {
              type: "category",
              labels: uniqueYears.map(String),
              title: { display: true, text: "Year" }
            }
          }
        }
      },
      scatter: {
        type: "scatter",
        data: {
          datasets: [
            {
              label: "ESG vs Market Cap",
              data: displayedData.map((d) => ({
                CompanyName: d.CompanyName,
                Year: d.Year,
                x: d.ESG_Overall,
                y: d.MarketCap,
              })),
              pointRadius: 5,
              backgroundColor: "rgba(255, 99, 132, 0.6)",
              borderColor: "rgb(255, 99, 132)",
            },
          ],
        },
        options: {
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  const data = context.raw;
                  return [
                    `Company: ${data.CompanyName}`,
                    `Year: ${data.Year}`,
                    `ESG Score: ${data.x}`,
                    `Market Cap: ${data.y.toLocaleString()}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: "ESG Score",
              },
            },
            y: {
              min: 0,
              max: 100000,
              title: {
                display: true,
                text: "Market Cap",
              },
            },
          },
        }
      },
      bubble: {
        type: "bubble",
        data: {
          datasets: [
            {
              label: "Revenue vs Profit Margin",
              data: displayedData.map((d) => ({
                CompanyName: d.CompanyName,
                Year: d.Year,
                x: d.Revenue,
                y: d.ProfitMargin,
                ESG_Overall: d.ESG_Overall,
                r:
                  ((d.ESG_Overall - esgMin) / (esgMax - esgMin || 1)) *
                  (maxRadius - minRadius) +
                  minRadius,
              })),
              backgroundColor: "rgba(54, 162, 235, 0.5)",
              borderColor: "rgb(54, 162, 235)",
            },
          ],
        },
        options: {
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  const data = context.raw;
                  return [
                    `Company: ${data.CompanyName}`,
                    `Year: ${data.Year}`,
                    `Revenue: ${data.x}`,
                    `Profit Margin: ${data.y}%`,
                    `ESG Score: ${data.ESG_Overall}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              min: 0,
              max: 10000,
              title: {
                display: true,
                text: "Revenue",
              },
            },
            y: {
              title: {
                display: true,
                text: "Profit Margin (%)",
              },
            },
          },
        }
      }
    }
  }, [displayedData]);

  const buildScatterConfig = (name, xField, yField) => ({
    type: "scatter",
    data: {
      datasets: [
        {
          label: name || `${yField} vs ${xField}`,
          backgroundColor: "rgba(75, 181, 67, 0.6)",
          borderColor: "rgb(30, 130, 76)",
          pointRadius: 5,
          data: displayedData.map((d) => ({
            CompanyName: d.CompanyName,
            Year: d.Year,
            x: d[xField],
            y: d[yField],
          })),
        },
      ],
    },
    options: {
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              const data = context.raw;
              return [
                `Company: ${data.CompanyName}`,
                `Year: ${data.Year}`,
                `${xField}: ${data.x}`,
                `${yField}: ${data.y}`,
              ];
            },
          },
        },
      },
      scales: {
        x: { title: { display: true, text: xField } },
        y: { title: { display: true, text: yField } },
      },
    },
  });

  const buildBubbleConfig = (name, xField, yField, rField = "ESG_Overall") => {
    const minRadius = 5;
    const maxRadius = 30;
    const esgMin = displayedData.length > 0 ? Math.min(...displayedData.map(d => d[rField] || 0)) : 0;
    const esgMax = displayedData.length > 0 ? Math.max(...displayedData.map(d => d[rField] || 0)) : 1;

    return {
      type: "bubble",
      data: {
        datasets: [
          {
            label: name || `${yField} vs ${xField}`,
            backgroundColor: "rgba(255, 159, 64, 0.5)",
            borderColor: "rgb(255, 127, 0)",
            data: displayedData.map((d) => ({
              CompanyName: d.CompanyName,
              Year: d.Year,
              x: d[xField],
              y: d[yField],
              ESG_Overall: d[rField],
              r:
                ((d[rField] - esgMin) / (esgMax - esgMin || 1)) *
                (maxRadius - minRadius) +
                minRadius,
            })),
          },
        ],
      },
      options: {
        plugins: {
          tooltip: {
            callbacks: {
              label: function (context) {
                const data = context.raw;
                return [
                  `Company: ${data.CompanyName}`,
                  `Year: ${data.Year}`,
                  `${xField}: ${data.x}`,
                  `${yField}: ${data.y}`,
                  `${rField}: ${data.ESG_Overall}`,
                ];
              },
            },
          },
        },
        scales: {
          x: { title: { display: true, text: xField } },
          y: { title: { display: true, text: yField } },
        },
      },
    };
  };

  const buildBarConfig = (name, xField, yField) => {
    return {
      name,
      type: "bar",
      data: displayedData || [],
      series: [
        {
          type: "bar",
          direction: "horizontal",
          xKey: "CompanyName",
          yKey: xField,
          yName: xField,
        },
        {
          type: "bar",
          direction: "horizontal",
          xKey: "CompanyName",
          yKey: yField,
          yName: yField,
        },
      ],
      axes: [
        {
          type: "number",
          position: "top",
          max: 10000000,
        },
        {
          type: "category",
          position: "left",
        },
      ],
    };
  };

  const buildRadialConfig = (name, metricField) => {
    if (displayedData.length === 0) {
      return {
        name,
        type: "radial",
        value: 0
      };
    }

    // Extract all values for the selected metric
    const values = displayedData
      .map(d => d[metricField])
      .filter(v => typeof v === "number");

    const min = Math.min(...values);
    const max = Math.max(...values);

    // Raw average
    const rawAvg =
      values.reduce((sum, v) => sum + v, 0) / values.length;

    // Normalize to 0–100%
    let normalized = 0;

    if (max === min) {
      normalized = 100; // All values equal = full gauge
    } else {
      normalized = ((rawAvg - min) / (max - min)) * 100;
    }

    // Clamp 0–100
    const value = Math.max(0, Math.min(100, normalized));

    return {
      name: name,
      type: "radial",
      value,
      labels: {
        tickLabels: {
          type: "inner",
          ticks: [{ value: 20 }, { value: 40 }, { value: 60 }, { value: 80 }, { value: 100 }],
        },
      },
      arc: {
        colorArray: ['#5BE12C', '#EA4228'],
        subArcs: [{ limit: 20 }, { limit: 40 }, { limit: 60 }, { limit: 80 }, { limit: 100 }],
        padding: 0.02,
        width: 0.3,
      },
      pointer: { elastic: true, animationDelay: 0 },
    };
  };

  const buildStackBarConfig = (name, xField, yField) => {
    return {
      data: Array.isArray(displayedData) ? displayedData : [],
      type: "stackedBar",
      name: name,
      series: [
        {
          type: "bar",
          xKey: "CompanyName",
          yKey: xField,
          yName: xField,
          stacked: true,
        },
        {
          type: "bar",
          xKey: "CompanyName",
          yKey: yField,
          yName: yField,
          stacked: true,
        },
      ],
      axes: [
        { type: "category", position: "bottom" },
        { type: "number", position: "left" }
      ],
      legend: { position: "bottom" },
    }
  }

  const buildHeatmapConfig = (name, field) => {
    const uniqueCompanies = [...new Set(displayedData.map(d => d.CompanyName))];
    const uniqueYears = [...new Set(displayedData.map(d => d.Year))];

    // Extract all valid numbers for scaling
    const values = displayedData.map(d => d[field]).filter(v => typeof v === "number");

    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 1;

    const scaleColor = (v) => {
      if (v === null || v === undefined) return "transparent";
      if (max === min) return "#b2df8a";
      const t = (v - min) / (max - min);
      if (t < 0.33) return "#4CAF50";
      if (t < 0.66) return "#FFEB3B";
      if (t < 0.85) return "#FF9800";
      return "#D32F2F";
    };

    return {
      type: "matrix",
      name: name || "Heatmap",

      data: {
        datasets: [
          {
            data: displayedData.map((d) => ({
              x: d.CompanyName,
              y: String(d.Year),
              v: d[field] ?? null,
            })),

            width: ({ chart }) => {
              const area = chart?.chartArea;
              if (!area) return 10;
              return area.width / uniqueCompanies.length - 2;
            },

            height: ({ chart }) => {
              const area = chart?.chartArea;
              if (!area) return 10;
              return area.height / uniqueYears.length - 1;
            },

            backgroundColor(ctx) {
              return scaleColor(ctx.raw.v);
            },
          },
        ],
      },

      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label(ctx) {
                const raw = ctx.raw;
                return [
                  `Company: ${raw.x}`,
                  `Year: ${raw.y}`,
                  `${field}: ${raw.v}`
                ];
              },
            },
          },
        },

        scales: {
          x: {
            type: "category",
            labels: uniqueCompanies,
          },
          y: {
            type: "category",
            labels: uniqueYears.map(String),
          },
        },
      },
    };
  };


  const mergedChartConfigs = { ...baseChartConfigs, ...dynamicConfigs };

  useEffect(() => {
    setDisplayedData(esgData || []);
  }, [esgData]);

  return (
    <div className="dashboard-main">
      <Breadcrumbs />
      <div className="card-wrapper">
        <Card />
      </div>
      <div className="chart-wrapper">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={chartOrder} strategy={rectSortingStrategy}>
            <div className="chart-grid">
              {chartOrder.map((chartKey) => (
                <SortableChartCard key={chartKey} id={chartKey}>
                  {!visibleCharts[chartKey] ? (
                    renderCard(chartKey, null, null)
                  ) : (() => {
                    const cfg = dynamicConfigs[chartKey] || baseChartConfigs[chartKey];

                    /** Heatmap */
                    if (cfg.type === "matrix") {
                      return renderCard(
                        chartKey,
                        <div className="chart-container">
                          <Chart type="matrix" data={cfg.data} options={cfg.options} />
                        </div>,
                        cfg?.name || ""
                      );
                    }

                    /** Radial Gauge */
                    if (cfg.type === "radial") {
                      return renderCard(
                        chartKey,
                        <GaugeComponent {...cfg} />,
                        cfg?.name || ""
                      );
                    }

                    /** Scatter */
                    if (cfg.type === "scatter") {
                      return renderCard(
                        chartKey,
                        <div className="chart-container">
                          <Scatter data={cfg.data} options={cfg.options} />
                        </div>,
                        cfg?.name || ""
                      );
                    }

                    /** Bubble */
                    if (cfg.type === "bubble") {
                      return renderCard(
                        chartKey,
                        <div className="chart-container">
                          <Bubble data={cfg.data} options={cfg.options} />
                        </div>,
                        cfg?.name || ""
                      );
                    }

                    /** Bar */
                    if (cfg.type === "bar") {
                      return renderCard(
                        chartKey,
                        <div className="chart-container">
                          <AgCharts options={cfg} />
                        </div>,
                        cfg?.name || ""
                      );
                    }

                    /** Stacked Bar */
                    if (cfg?.type === "stackedBar") {
                      return renderCard(
                        chartKey,
                        <div className="chart-container">
                          <AgCharts options={cfg} />
                        </div>,
                        cfg?.name || ""
                      );
                    }
                    return renderCard(chartKey, <div>Unsupported chart type</div>, chartKey);
                  })()}
                </SortableChartCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
      <div className="grid-wrapper">
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={esgData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            onFilterChanged={updateDisplayedData}
            onSortChanged={updateDisplayedData}
            pagination={true}
            paginationPageSize={20}
            paginationPageSizeSelector={[10, 20, 50, 100]}
          />
        </div>
      </div>
      {showModal && (
        <CustomModal showCloseIcon handleClose={handleClose} modalTitle={mergedChartConfigs[activeChart]?.name || ""}>
          {(() => {
            const cfg = mergedChartConfigs[activeChart];
            if (!cfg) return null;

            if (cfg.type === "scatter") {
              return (
                <div className="chart-modal">
                  <Scatter data={cfg.data} options={cfg.options} />
                </div>
              );
            }

            if (cfg.type === "bubble") {
              return (
                <div className="chart-modal">
                  <Bubble data={cfg.data} options={cfg.options} />
                </div>
              );
            }

            if (cfg.type === "radial") {
              return (
                <div className="chart-modal" style={{ height: "520px", width: "700px" }}>
                  <GaugeComponent {...cfg} />
                </div>
              );
            }

            if (cfg.type === "matrix") {
              return (
                <div className="chart-modal">
                  <Chart type="matrix" data={cfg.data} options={cfg.options} />
                </div>
              );
            }

            if (cfg.type === "bar") {
              return (
                <div className="chart-modal" style={{ height: "400px", width: "1000px" }}>
                  <AgCharts options={cfg} />
                </div>
              );
            }

            if (cfg.type === "stackedBar") {
              return (
                <div className="chart-modal" style={{ height: "400px", width: "1000px" }}>
                  <AgCharts options={cfg} />
                </div>
              );
            }

            return <div>Unsupported chart type</div>;
          })()}
        </CustomModal>
      )}
      {showAddChartModal && (
        <CustomModal showCloseIcon handleClose={() => setShowAddChartModal(false)} modalTitle={"Add New Chart"}>
          <AddChartModal handleCreateChart={(values) => handleCreateChart(values)} />
        </CustomModal>
      )}
    </div>
  )
}

export default Dashboard;